import {LobbyImage} from '@colonist/types'
import {NotificationData, NotificationTypeData, NotificationTypeEnum, NotificationUserDetails} from '../../../../shared/NotificationTypes'
import {NotificationItemViewData, UINotificationItemViewBase} from './UINotificationItemViewBase'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'
import {UIUtilsNotifications} from './UIUtilsNotifications'
import {getNestedTranslation, TranslationObject} from '@colonist/i18n'
import {StoreItemController} from '../../../../shared/store/StoreItemController'

export class UINotificationItemView extends UINotificationItemViewBase {
    protected notificationUserDetails: NotificationUserDetails | undefined
    private readonly onClose?: () => void

    constructor(notificationData: NotificationData, parentContainer: HTMLElement) {
        super(notificationData.id, notificationData.dateReceived, parentContainer)

        this.notificationUserDetails = notificationData.notificationUserDetails

        this.viewData = this.getViewData(notificationData.typeData, this.notificationUserDetails)

        this.createBaseNotification(notificationData.dateReceived)
        this.showUnreadIconIfNeeded(notificationData.dateRead)
        this.setClickActions()
        this.removeContentHoverEffectIfNoClickAction()
    }

    setOnClose(callback: () => void) {
        this.setOnClose = callback
    }

    private getViewData(notificationTypeData: NotificationTypeData, notificationUserDetails: NotificationUserDetails | undefined): NotificationItemViewData {
        switch(notificationTypeData.type) {
            case NotificationTypeEnum.UserReportedBanned:
                return {
                    text: {key: 'strings:popups.notification.reportedUserBanned.body'},
                    image: LobbyImage.Guest,
                }
            case NotificationTypeEnum.UserReportedThankYou:
                return {
                    text: {key: 'strings:popups.notification.reportedUserThankYou.body'},
                    image: LobbyImage.Guest,
                }
            case NotificationTypeEnum.HolidaySale2023Expired:
                return {
                    text: {key: 'strings:popups.notification.holidaySale2023Expired.body'},
                    clickAction: () => this.onClose?.(),
                    image: LobbyImage.Guest,
                    secondaryLink: {
                        url: '/store',
                        openNewTab: true,
                        text: {key: 'strings:popups.notification.giftMembershipReceived.checkButton', options: {context: UIUtilsEjsVariables.isUserOnDiscord ? 'discord' : ''}},
                    },
                }
            case NotificationTypeEnum.GiftMembershipReceivedFromAdmin:
                return {
                    text: {key: 'strings:popups.notification.giftMembershipReceived.body'},
                    clickAction: () => this.onClose?.(),
                    image: LobbyImage.Guest,
                    secondaryLink: {
                        url: '/store#gifted_memberships',
                        openNewTab: true,
                        text: {key: 'strings:popups.notification.giftMembershipReceived.checkButton', options: {context: UIUtilsEjsVariables.isUserOnDiscord ? 'discord' : ''}},
                    },
                }
            case NotificationTypeEnum.TranslationHelp:
                return {
                    text: {key: 'strings:popups.notification.translationHelp.body', options: {language: notificationTypeData.language, username: notificationTypeData.username, link: UIUtilsEjsVariables.isUserOnMobileAppOrWeb ? undefined : notificationTypeData.link}},
                    image: LobbyImage.Guest,
                }
            case NotificationTypeEnum.UserReportedWarning:
                return {
                    text: {key: 'strings:popups.notification.reportedUserWarning'},
                    image: LobbyImage.DangerIcon,
                }
            case NotificationTypeEnum.RoomInviteResponse:
                return {
                    text: {key: 'strings:popups.notification.youRespondedToRoomInviteFrom', options: {username: notificationUserDetails?.username, context: notificationTypeData.response ? undefined : 'declined'}},
                    image: notificationUserDetails?.icon ?? LobbyImage.Guest,
                }
            case NotificationTypeEnum.MissedRoomInvite:
                return {
                    text: {key: 'strings:popups.notification.youRespondedToRoomInviteFrom', options: {username: notificationUserDetails?.username, context: 'missed'}},
                    image: notificationUserDetails?.icon ?? LobbyImage.Guest,
                }
            case NotificationTypeEnum.YouAreNowFriendsWith:
                return {
                    text: {key: 'strings:popups.notification.youAreNowFriendsWith', options: {username: notificationUserDetails?.username}},
                    image: notificationUserDetails?.icon ?? LobbyImage.Guest,
                }
            case NotificationTypeEnum.GiftMembershipReceivedFromUser:
                const giftMessage = notificationTypeData?.giftMessage?.trim()
                return {
                    text: {
                        key: 'strings:popups.notification.giftMembershipReceivedFromFriend',
                        options: {
                            context: giftMessage ? '' : 'noMessage',
                            gifter: notificationUserDetails?.username,
                            giftMessage: giftMessage,
                        },
                    },
                    image: notificationUserDetails?.icon ?? LobbyImage.Guest,
                }
            case NotificationTypeEnum.GiftMembershipWasSent:
                const membershipTier = UIUtilsNotifications.getMembershipTitleFromType(notificationTypeData.membershipType)
                return {
                    text: getNestedTranslation({key: 'strings:popups.notification.giftMembershipWasSentToFriend', options: {duration: notificationTypeData.duration, username: notificationUserDetails?.username}}, {membershipType: membershipTier}),
                    image: notificationUserDetails?.icon ?? LobbyImage.Guest,
                }
            case NotificationTypeEnum.GiftURLCreated:
                const key = 'strings:popups.notification.giftURLCreated'
                const context = notificationTypeData.duration == 1 ? '' : 'multiple'
                const membershipType = UIUtilsNotifications.getMembershipTitleFromType(notificationTypeData.membershipType)
                return {
                    text: getNestedTranslation({key: key, options: {duration: notificationTypeData.duration, hash: notificationTypeData.hash, context: context}}, {membershipType: membershipType}),
                    image: notificationUserDetails?.icon ?? LobbyImage.Guest,
                }
            case NotificationTypeEnum.ReferralRewardItemUnlocked:
                const item = StoreItemController.getItemDetailForReferralProgram(notificationTypeData.itemCategory, notificationTypeData.itemType)
                if(item == undefined) throw new Error('Item does not exist')
                return {
                    text: getNestedTranslation({key: 'strings:popups.notification.referralRewardItemUnlocked.body', options: {itemName: item.name, referralCount: notificationTypeData.referralCount, context: notificationTypeData.referralCount == 1 ? 'one' : 'other'}}, {itemName: item.name}),
                    image: item.image,
                    secondaryLink: {
                        url: '/store#referrals',
                        openNewTab: true,
                        text: {key: 'strings:popups.notification.referralRewardItemUnlocked.checkButton'},
                    },
                }
            case NotificationTypeEnum.NewUserJoinedFromReferral:
                return {
                    text: {key: 'strings:popups.notification.newUserJoinedFromReferral.body', options: {referralLeftCount: notificationTypeData.referralLeftCount, username: notificationTypeData.username, context: notificationTypeData.referralLeftCount == 1 ? 'one' : 'other'}},
                    image: LobbyImage.StoreGiftMembership,
                    secondaryLink: {
                        url: '/store#referrals',
                        openNewTab: true,
                        text: {key: 'strings:popups.notification.newUserJoinedFromReferral.checkButton'},
                    },
                }
            case NotificationTypeEnum.YouAreBanned:
                const bannedUntil = new Date(notificationTypeData.untilDate).toDateString()
                const message: TranslationObject = {
                    key: 'strings:popups.errors.userBannedError.youAreBanned',
                    options: {
                        untilDate: ` until ${bannedUntil}`,
                    },
                }
                return {
                    text: message,
                    image: LobbyImage.DangerIcon,
                }
            case NotificationTypeEnum.GeneralAnnouncement:
                return {
                    text: {key: 'strings:colonistCommon.utils.#buffer', options: {value: notificationTypeData.message}},
                    image: LobbyImage.GameModeBase,
                }
            case NotificationTypeEnum.YourFriendMustLogInToCountReferral:
                return {
                    text: {key: 'strings:popups.notification.yourFriendMustLogInToCountReferral.body', options: {username: notificationTypeData.username}},
                    image: LobbyImage.Guest,
                }
        }
    }
}
